import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config/gameConfig';
import { loadSettings, saveSettings, BACKGROUNDS, type GameSettings, type BackgroundId } from '@/config/settings';
import { THEME } from '@/config/theme';
import { drawArcadeBackground, addTextGlow } from '@/utils/arcadeBackground';

export class SettingsScene extends Phaser.Scene {
  private settings!: GameSettings;
  private dotKeyText!: Phaser.GameObjects.Text;
  private dashKeyText!: Phaser.GameObjects.Text;
  private sfxSlider!: Phaser.GameObjects.Rectangle;
  private sfxFill!: Phaser.GameObjects.Rectangle;
  private musicSlider!: Phaser.GameObjects.Rectangle;
  private musicFill!: Phaser.GameObjects.Rectangle;
  private waitingForKey: 'dot' | 'dash' | null = null;
  private keyPromptText!: Phaser.GameObjects.Text;
  private keyPromptBg!: Phaser.GameObjects.Rectangle;
  private bgButtons: Map<BackgroundId, Phaser.GameObjects.Rectangle> = new Map();

  constructor() {
    super({ key: 'SettingsScene' });
  }

  create(): void {
    // Fade in
    this.cameras.main.fadeIn(300, 5, 7, 22);

    // Arcade background
    drawArcadeBackground(this);

    this.settings = loadSettings();

    // Title with glow
    const title = this.add.text(GAME_WIDTH / 2, 60, 'SETTINGS', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.title,
      color: THEME.colors.cyan,
    }).setOrigin(0.5);
    addTextGlow(title, THEME.colors.cyan, 15);

    // Key bindings section
    this.createKeyBindingsSection();

    // Volume section
    this.createVolumeSection();

    // Background section
    this.createBackgroundSection();

    // Key prompt modal (hidden initially)
    this.keyPromptBg = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, 350, 120, THEME.colors.backgroundPanelHex, 1);
    this.keyPromptBg.setStrokeStyle(3, THEME.colors.cyanHex);
    this.keyPromptBg.setVisible(false).setDepth(99);

    this.keyPromptText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, '', {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.heading,
      color: THEME.colors.cyan,
    }).setOrigin(0.5).setVisible(false).setDepth(100);

    // Back button
    this.createButton(100, 60, '← BACK', () => {
      this.transitionTo('MenuScene');
    }, true);

    // Listen for key presses when rebinding
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      this.handleKeyPress(event);
    });
  }

  private createKeyBindingsSection(): void {
    const startY = 150;

    this.add.text(GAME_WIDTH / 2, startY, 'KEY BINDINGS', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5);

    // Dot key
    this.add.image(GAME_WIDTH / 2 - 220, startY + 50, 'dot-sprite').setOrigin(0.5).setScale(0.7);
    this.add.text(GAME_WIDTH / 2 - 160, startY + 50, 'Dot', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thicknessThin,
    }).setOrigin(0, 0.5);

    this.dotKeyText = this.add.text(GAME_WIDTH / 2 + 100, startY + 50, this.settings.dotKey, {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.primary,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5).setDepth(1);

    this.createKeyButton(GAME_WIDTH / 2 + 100, startY + 50, 'dot');

    // Dash key
    this.add.image(GAME_WIDTH / 2 - 220, startY + 100, 'dash-sprite').setOrigin(0.5).setScale(0.7);
    this.add.text(GAME_WIDTH / 2 - 160, startY + 100, 'Dash', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thicknessThin,
    }).setOrigin(0, 0.5);

    this.dashKeyText = this.add.text(GAME_WIDTH / 2 + 100, startY + 100, this.settings.dashKey, {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.primary,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5).setDepth(1);

    this.createKeyButton(GAME_WIDTH / 2 + 100, startY + 100, 'dash');
  }

  private createKeyButton(x: number, y: number, type: 'dot' | 'dash'): void {
    const bg = this.add.rectangle(x, y, 80, 40, THEME.colors.backgroundPanelHex, 1);
    bg.setStrokeStyle(2, THEME.colors.borderHex);
    bg.setInteractive({ useHandCursor: true });

    bg.on('pointerover', () => bg.setStrokeStyle(2, THEME.colors.primaryHex));
    bg.on('pointerout', () => bg.setStrokeStyle(2, THEME.colors.borderHex));
    bg.on('pointerdown', () => {
      this.waitingForKey = type;
      this.keyPromptText.setText(`Press a key for ${type}...`);
      this.keyPromptBg.setVisible(true);
      this.keyPromptText.setVisible(true);
    });
  }

  private handleKeyPress(event: KeyboardEvent): void {
    // Handle Escape to go back to menu (when not rebinding)
    if (!this.waitingForKey && event.key === 'Escape') {
      this.transitionTo('MenuScene');
      return;
    }

    if (!this.waitingForKey) return;

    // Ignore modifier keys
    if (['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) return;

    const key = event.key.toUpperCase();

    if (this.waitingForKey === 'dot') {
      this.settings.dotKey = key;
      this.dotKeyText.setText(key);
    } else {
      this.settings.dashKey = key;
      this.dashKeyText.setText(key);
    }

    saveSettings(this.settings);
    this.waitingForKey = null;
    this.keyPromptBg.setVisible(false);
    this.keyPromptText.setVisible(false);
  }

  private createVolumeSection(): void {
    const startY = 320;

    this.add.text(GAME_WIDTH / 2, startY, 'VOLUME', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5);

    // SFX volume
    this.add.text(GAME_WIDTH / 2 - 150, startY + 50, 'Sound Effects', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thicknessThin,
    }).setOrigin(0, 0.5);

    const sfxSliderResult = this.createSlider(
      GAME_WIDTH / 2 + 80,
      startY + 50,
      this.settings.sfxVolume,
      (value) => {
        this.settings.sfxVolume = value;
        saveSettings(this.settings);
      }
    );
    this.sfxSlider = sfxSliderResult.track;
    this.sfxFill = sfxSliderResult.fill;

    // Music volume
    this.add.text(GAME_WIDTH / 2 - 150, startY + 100, 'Music', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thicknessThin,
    }).setOrigin(0, 0.5);

    const musicSliderResult = this.createSlider(
      GAME_WIDTH / 2 + 80,
      startY + 100,
      this.settings.musicVolume,
      (value) => {
        this.settings.musicVolume = value;
        saveSettings(this.settings);
      }
    );
    this.musicSlider = musicSliderResult.track;
    this.musicFill = musicSliderResult.fill;
  }

  private createBackgroundSection(): void {
    const startY = 490;

    this.add.text(GAME_WIDTH / 2, startY, 'BACKGROUND', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5);

    const buttonWidth = 100;
    const spacing = 20;
    const totalWidth = BACKGROUNDS.length * buttonWidth + (BACKGROUNDS.length - 1) * spacing;
    const startX = (GAME_WIDTH - totalWidth) / 2 + buttonWidth / 2;

    BACKGROUNDS.forEach((bg, index) => {
      const x = startX + index * (buttonWidth + spacing);
      const y = startY + 50;
      const isSelected = this.settings.background === bg.id;

      const btnBg = this.add.rectangle(x, y, buttonWidth, 40,
        isSelected ? THEME.colors.primaryHex : THEME.colors.backgroundPanelHex, 1);
      btnBg.setStrokeStyle(2, isSelected ? THEME.colors.primaryDarkHex : THEME.colors.borderHex);
      btnBg.setInteractive({ useHandCursor: true });

      this.add.text(x, y, bg.label, {
        fontFamily: THEME.fonts.pixel,
        fontSize: THEME.sizes.small,
        color: isSelected ? THEME.colors.textOnPrimary : THEME.colors.text,
      }).setOrigin(0.5);

      this.bgButtons.set(bg.id, btnBg);

      btnBg.on('pointerover', () => {
        if (this.settings.background !== bg.id) {
          btnBg.setStrokeStyle(2, THEME.colors.primaryHex);
        }
      });
      btnBg.on('pointerout', () => {
        if (this.settings.background !== bg.id) {
          btnBg.setStrokeStyle(2, THEME.colors.borderHex);
        }
      });
      btnBg.on('pointerdown', () => {
        this.selectBackground(bg.id);
      });
    });
  }

  private selectBackground(id: BackgroundId): void {
    this.settings.background = id;
    saveSettings(this.settings);
    // Restart scene to show new background
    this.scene.restart();
  }

  private createSlider(
    x: number,
    y: number,
    initialValue: number,
    onChange: (value: number) => void
  ): { track: Phaser.GameObjects.Rectangle; fill: Phaser.GameObjects.Rectangle } {
    const width = 150;
    const height = 20;

    // Track background
    const track = this.add.rectangle(x, y, width, height, THEME.colors.backgroundPanelHex, 1);
    track.setStrokeStyle(2, THEME.colors.borderHex);
    track.setOrigin(0, 0.5);

    // Fill
    const fill = this.add.rectangle(x + 2, y, (width - 4) * initialValue, height - 4, THEME.colors.primaryHex, 1);
    fill.setOrigin(0, 0.5);

    // Value text
    const valueText = this.add.text(x + width + 15, y, `${Math.round(initialValue * 100)}%`, {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.small,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thicknessThin,
    }).setOrigin(0, 0.5);

    // Make interactive
    track.setInteractive({ useHandCursor: true });

    const updateSlider = (pointerX: number) => {
      const relativeX = pointerX - x;
      const value = Phaser.Math.Clamp(relativeX / width, 0, 1);
      fill.width = (width - 4) * value;
      valueText.setText(`${Math.round(value * 100)}%`);
      onChange(value);
    };

    track.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      updateSlider(pointer.x);
    });

    track.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (pointer.isDown) {
        updateSlider(pointer.x);
      }
    });

    return { track, fill };
  }

  private createButton(x: number, y: number, text: string, onClick: () => void, small = false): void {
    const width = small ? 120 : 200;
    const height = small ? 40 : 50;
    const fontSize = small ? THEME.sizes.small : THEME.sizes.bodyLarge;

    const bg = this.add.rectangle(x, y, width, height, THEME.colors.primaryHex, 1);
    bg.setStrokeStyle(2, THEME.colors.primaryDarkHex);

    this.add.text(x, y, text, {
      fontFamily: THEME.fonts.pixelBold,
      fontSize,
      color: THEME.colors.textOnPrimary,
    }).setOrigin(0.5);

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setFillStyle(THEME.colors.primaryDarkHex));
    bg.on('pointerout', () => bg.setFillStyle(THEME.colors.primaryHex));
    bg.on('pointerdown', onClick);
  }

  private transitionTo(scene: string, data?: object): void {
    this.cameras.main.fadeOut(200, 5, 7, 22);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(scene, data);
    });
  }
}
